import { action, observable, reaction } from 'mobx';
import { type DependencyList, useCallback, useEffect, useState } from 'react';
import {
  useHotkeys as useReactHotkeys,
  type HotkeyCallback,
  type Keys,
  type Options,
} from 'react-hotkeys-hook';

import type { KeyName } from '@/lib/key';
import { settingsStore } from '@/stores/settings.store';

import { getShortcutDefinition, type ShortcutId } from './shortcuts.registry';
import { formatShortcutKeys, parseShortcutString } from './shortcuts.utils';

type AppShortcutOptions = Options & {
  combo?: Keys;
};

const MODIFIER_KEYS = new Set(['Control', 'Meta', 'Shift', 'Alt']);

const shortcutRecordingActive = observable.box(false);

export const setShortcutRecordingActive = action((active: boolean) => {
  shortcutRecordingActive.set(active);
});

const emptyShortcutSettings = {
  enabled: false,
  combo: undefined,
};

function readShortcutSettings(id?: ShortcutId) {
  if (!id) {
    return emptyShortcutSettings;
  }

  return {
    enabled:
      typeof settingsStore.isShortcutEnabled === 'function'
        ? settingsStore.isShortcutEnabled(id)
        : true,
    combo:
      typeof settingsStore.getShortcutCombo === 'function'
        ? settingsStore.getShortcutCombo(id)
        : undefined,
  };
}

function useShortcutSettings(id?: ShortcutId) {
  const [settings, setSettings] = useState(() => readShortcutSettings(id));

  useEffect(() => {
    if (!id) return;

    return reaction(
      () => readShortcutSettings(id),
      (nextSettings) => setSettings(nextSettings),
      { fireImmediately: true }
    );
  }, [id]);

  return settings;
}

export function useShortcutKeys(
  id?: ShortcutId,
  fallbackKeys?: readonly KeyName[]
): readonly KeyName[] | undefined {
  const shortcutSettings = useShortcutSettings(id);
  if (!id) return undefined;

  const definition = getShortcutDefinition(id);

  if (!shortcutSettings.enabled) return undefined;
  return shortcutSettings.combo
    ? parseShortcutString(shortcutSettings.combo)
    : (fallbackKeys ?? definition.keys);
}

export function useShortcutLabel(
  id: ShortcutId,
  fallbackKeys?: readonly KeyName[]
): string | undefined {
  return formatShortcutKeys(useShortcutKeys(id, fallbackKeys));
}

export function useAppShortcut<T extends HTMLElement>(
  id: ShortcutId,
  callback: HotkeyCallback,
  options: AppShortcutOptions = {},
  dependencies?: DependencyList
) {
  const shortcutSettings = useShortcutSettings(id);
  const definition = getShortcutDefinition(id);
  const { combo, enabled, ignoreEventWhen, ...hotkeyOptions } = options;
  // react-hotkeys-hook re-registers its listener, and under a HotkeysProvider
  // re-renders, whenever an option changes, so this function must stay stable.
  const ignoreModifierOrCaller = useCallback(
    (event: KeyboardEvent) =>
      MODIFIER_KEYS.has(event.key) || (ignoreEventWhen?.(event) ?? false),
    [ignoreEventWhen]
  );

  return useReactHotkeys<T>(
    shortcutSettings.combo ?? combo ?? definition.defaultCombo,
    (event, hotkey) => {
      if (shortcutRecordingActive.get()) return;
      callback(event, hotkey);
    },
    {
      ...hotkeyOptions,
      // react-hotkeys-hook matches a bare Ctrl or Cmd keydown while its pressed-key
      // set still holds the combo's letter (a held or unreleased key), so only the
      // keydown of the combo's own key may fire a shortcut.
      ignoreEventWhen: ignoreModifierOrCaller,
      enabled: shortcutSettings.enabled ? enabled : false,
    },
    dependencies
  );
}
